import { KeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { focusPickerControl } from '../helpers/pickerPopupHelper';

/** The popup mounts and aligns over a few frames, so focusing into it is retried until it exists. */
const FOCUS_ATTEMPTS = 30;

interface PickerPopupKeyboardOptions {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  label: string;
  getInput: () => HTMLInputElement | null | undefined;
  /** The control inside the popup that should hold focus now, such as the active date. */
  getActiveControl: (popup: HTMLElement) => HTMLElement | null;
  /** The controls Tab visits inside the popup, in order. */
  getTabStops: (popup: HTMLElement) => HTMLElement[];
  onControlKeyDown: (
    event: KeyboardEvent<HTMLElement>,
    focusActiveControlAfterRender: () => void,
  ) => void;
}

export const takeOverKey = (event: Pick<KeyboardEvent, 'preventDefault' | 'stopPropagation'>) => {
  event.preventDefault();
  event.stopPropagation();
};

const nextStopIndex = (current: number, count: number, backwards: boolean) => {
  if (current === -1) return backwards ? count - 1 : 0;
  return (current + (backwards ? count - 1 : 1)) % count;
};

/**
 * Makes an rc-picker popup a keyboard dialog: focus goes inside when it is opened from the
 * keyboard, Tab cycles through its controls, Escape closes it, and focus goes back to whatever
 * opened it when it closes with focus inside.
 */
export const usePickerPopupKeyboard = ({
  isOpen,
  setOpen,
  label,
  getInput,
  getActiveControl,
  getTabStops,
  onControlKeyDown,
}: PickerPopupKeyboardOptions) => {
  const popupRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const pendingFocusRef = useRef(false);
  const returnFocusRef = useRef(false);

  useLayoutEffect(() => {
    if (!isOpen || !pendingFocusRef.current) return undefined;
    let attemptsLeft = FOCUS_ATTEMPTS;
    let frame = 0;
    const focusActiveControl = () => {
      const popup = popupRef.current;
      const control = popup && getActiveControl(popup);
      if (control) focusPickerControl(control);
      // A reopened popup is still hidden by its closing animation for a frame or two.
      if (control && document.activeElement === control) {
        pendingFocusRef.current = false;
        return;
      }
      attemptsLeft -= 1;
      if (attemptsLeft > 0) frame = requestAnimationFrame(focusActiveControl);
    };
    focusActiveControl();
    return () => cancelAnimationFrame(frame);
  });

  useEffect(() => {
    if (isOpen || !returnFocusRef.current) return;
    returnFocusRef.current = false;
    const opener = openerRef.current?.isConnected ? openerRef.current : getInput();
    opener?.focus();
  }, [isOpen]);

  /** Focuses the active control on the next render, once state it depends on has changed. */
  const focusActiveControlAfterRender = () => {
    pendingFocusRef.current = true;
  };

  const focusInside = () => {
    const active = document.activeElement;
    if (!popupRef.current?.contains(active)) {
      openerRef.current = active instanceof HTMLElement ? active : null;
    }
    const control = isOpen && popupRef.current ? getActiveControl(popupRef.current) : null;
    if (control) {
      focusPickerControl(control);
      return;
    }
    pendingFocusRef.current = true;
    setOpen(true);
  };

  /** Call from the picker's onOpenChange: a close with focus inside must hand focus back. */
  const noteOpenChange = (open: boolean) => {
    if (!open && popupRef.current?.contains(document.activeElement)) {
      returnFocusRef.current = true;
    }
  };

  const closeAndReturnFocus = () => {
    returnFocusRef.current = true;
    setOpen(false);
  };

  const moveThroughTabStops = (popup: HTMLElement, backwards: boolean) => {
    const stops = getTabStops(popup);
    if (stops.length === 0) return;
    const current = stops.indexOf(document.activeElement as HTMLElement);
    focusPickerControl(stops[nextStopIndex(current, stops.length, backwards)]);
  };

  const onPopupKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      takeOverKey(event);
      closeAndReturnFocus();
      return;
    }
    if (event.key === 'Tab') {
      takeOverKey(event);
      moveThroughTabStops(event.currentTarget, event.shiftKey);
      return;
    }
    onControlKeyDown(event, focusActiveControlAfterRender);
  };

  const panelRender = (panel: ReactNode) => (
    <dialog
      open
      ref={popupRef}
      className="popup-dialog"
      aria-modal="true"
      aria-label={label}
      onKeyDown={onPopupKeyDown}
    >
      {panel}
    </dialog>
  );

  return { panelRender, focusInside, noteOpenChange };
};
